/**
 * useTemplateLoader Hook
 * Phase 2 Sprint 14: Template Loading Workflow
 * 
 * Hook for loading templates into the Canvas Recorder
 */

import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { 
  loadedTemplateAtom, 
  templateLoadingAtom, 
  templateLoadErrorAtom 
} from '../atoms/templateAtoms';
import { loadProjectIntoStateAtom } from '../atoms/sceneAtoms';
import { Template } from '../../../types/template';
import { MultiSceneProject, Scene } from '../../../types/scene';
import logger from '../utils/logger';
import { db } from '../idb/chunk-db';
import { onboardingService } from '../services/onboardingService';

export interface UseTemplateLoaderReturn {
  loadTemplate: (template: Template) => Promise<void>;
  clearTemplate: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for loading templates into the recorder
 * 
 * Provides functions to load and clear templates,
 * with loading state and error handling
 */
export const useTemplateLoader = (): UseTemplateLoaderReturn => {
  const setLoadedTemplate = useSetAtom(loadedTemplateAtom);
  const setLoading = useSetAtom(templateLoadingAtom);
  const setError = useSetAtom(templateLoadErrorAtom);
  const loadProjectIntoState = useSetAtom(loadProjectIntoStateAtom);

  /**
   * Load a template into the recorder
   * 
   * This function:
   * 1. Validates the template
   * 2. Sets loading state
   * 3. Converts template to MultiSceneProject
   * 4. Loads project into scene state (Sprint 15-16 integration)
   * 5. Handles errors
   */
  const loadTemplate = useCallback(async (template: Template) => {
    try {
      setLoading(true);
      setError(null);

      // Validate template
      if (!template || !template.id) {
        throw new Error('Invalid template: missing required fields');
      }

      if (!template.scenes || template.scenes.length === 0) {
        throw new Error('Invalid template: no scenes found');
      }

      logger.info(`Loading template: ${template.id} (${template.title})`);

      // Simulate async loading
      await new Promise(resolve => setTimeout(resolve, 100));

      // Store the template
      setLoadedTemplate(template);

      // Sprint 15-16 Integration: Convert template to MultiSceneProject
      // Sprint 19+: Flow all metadata (notes, tags, durations) from template to scenes
      // Sprint 20+: Apply template background image to all scenes
      const scenes: Scene[] = template.scenes.map((templateScene, index) => {
        // Find matching script
        const sceneScript = template.scripts.find(
          (script) => script.key === templateScene.scriptKey
        );

        // Determine scene background: use template's backgroundImage if available, otherwise use scene's defined background
        const sceneBackground = template.backgroundImage
          ? { type: 'image' as const, imageUrl: template.backgroundImage }
          : templateScene.background;

        return {
          id: `scene-${template.id}-${index}`,
          order: index,
          title: templateScene.title,
          duration: 0,
          suggestedDuration: templateScene.suggestedDuration * 1000, // Convert seconds to ms
          videoChunks: [],
          layout: [], // Empty layout - will be populated when preset is applied
          layoutPreset: templateScene.layoutPreset, // Store preset name for later application
          background: sceneBackground,
          script: sceneScript?.content || '',
          status: 'pending' as const,
          recordedAt: undefined,
          thumbnail: undefined,
          // Sprint 19+: Flow enhanced metadata from template
          notes: templateScene.notes, // Pre-populated guidance/tips from template
          tags: templateScene.tags, // Pre-assigned semantic tags from template
          isExpanded: false, // Scenes start collapsed by default
          recordingHistory: [], // Empty until first recording attempt
          approvalStatus: undefined, // No status until recorded
        };
      });

      const project: MultiSceneProject = {
        id: `project-${template.id}`,
        title: template.title,
        templateId: template.id,
        scenes: scenes,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        totalDuration: 0,
        status: 'draft',
      };

      // CRITICAL FIX: Persist project to IndexedDB before loading into state
      // This ensures the project exists in the database when saving recordings
      try {
        // Check if project already exists
        const existingProject = await db.projects.get(project.id);
        
        if (!existingProject) {
          // Add project to database
          await db.projects.add(project);
          logger.info(`[useTemplateLoader] Project persisted to database: ${project.id}`);
          
          // Add all scenes to database
          for (const scene of scenes) {
            await db.scenes.add(scene);
          }
          logger.info(`[useTemplateLoader] ${scenes.length} scenes persisted to database`);
        } else {
          logger.info(`[useTemplateLoader] Project already exists in database: ${project.id}`);
          // Update existing project with latest data using put (replaces entire object)
          await db.projects.put(project);
          
          // CRITICAL FIX: Also update/add scenes when project already exists
          for (const scene of scenes) {
            const existingScene = await db.scenes.get(scene.id);
            if (existingScene) {
              await db.scenes.put(scene);
              logger.info(`[useTemplateLoader] Updated existing scene: ${scene.id}`);
            } else {
              await db.scenes.add(scene);
              logger.info(`[useTemplateLoader] Added new scene: ${scene.id}`);
            }
          }
          logger.info(`[useTemplateLoader] ${scenes.length} scenes updated/added to database`);
        }
      } catch (dbError) {
        logger.error('[useTemplateLoader] Failed to persist project to database:', dbError);
        throw new Error('Failed to save project to database');
      }

      // Load project into scene state
      loadProjectIntoState(project);

      // Mark onboarding task: user loaded a template-based project with scenes
      onboardingService.markCreateScenesProject().catch(() => {});

      logger.info(`✅ [useTemplateLoader] Template loaded successfully: ${template.id}`);
      logger.info(`✅ [useTemplateLoader] Multi-scene project created with ${project.scenes.length} scenes`);
      logger.info(`✅ [useTemplateLoader] First scene should be auto-selected:`, {
        firstSceneId: project.scenes[0]?.id,
        firstSceneTitle: project.scenes[0]?.title,
        totalScenes: project.scenes.length
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load template';
      logger.error('Template load error:', error);
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoadedTemplate, setLoading, setError, loadProjectIntoState]);

  /**
   * Clear the currently loaded template
   */
  const clearTemplate = useCallback(() => {
    setLoadedTemplate(null);
    setError(null);
    logger.info('Template cleared');
  }, [setLoadedTemplate, setError]);

  return {
    loadTemplate,
    clearTemplate,
    isLoading: false, // This will be connected to the atom in a future PR
    error: null // This will be connected to the atom in a future PR
  };
};

export default useTemplateLoader;
